import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Stack,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { SyntheticEvent, useMemo, useState } from "react";

import { GenericTable } from "../../../../../components/GenericTable/GenericTable";
import { PatientSelectedQuestionnaireDialog } from "src-next/modules/patient-summary/components/PatientSelectedQuestionnaireDialog";
import {
  CODE_TO_USAGE_TYPE,
  QUESTIONNAIRE_TITLE_EXTENSION_URL,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
} from "src-next/modules/questionnaire-builder/form-builder/utils/questionnaireQuestionnaireConstants";
import { QuestionnairesActions } from "./QuestionnairesActions";

const TOUCH_MIN_PX = 44;

type UsageTabValue =
  | "all"
  | "triage"
  | "preConsultation"
  | "postConsultation"
  | "attendance"
  | "others";

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
  { value: "others", label: "Outros" },
];

function a11yProps(index: number) {
  return {
    id: `questionnaires-tab-${index}`,
    "aria-controls": `questionnaires-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: {
  children: React.ReactNode;
  index: number;
  value: UsageTabValue;
}) {
  const { children, value, index, ...other } = props;
  const tabValue = USAGE_TABS[index]?.value;

  return (
    <div
      role="tabpanel"
      hidden={value !== tabValue}
      id={`questionnaires-tabpanel-${index}`}
      aria-labelledby={`questionnaires-tab-${index}`}
      {...other}
    >
      {value === tabValue && (
        <Box sx={{ maxHeight: "60vh", pt: 2, overflow: "auto" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function usageTypeFromQuestionnaireResponse(qr: any): {
  usageTypeDomain: UsageTabValue | undefined;
  usageTypeLabel: string;
} {
  const extensions = qr?.extension;
  if (!Array.isArray(extensions))
    return { usageTypeDomain: undefined, usageTypeLabel: "—" };

  const ext = extensions.find(
    (extension: any) =>
      extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  );
  if (!ext) return { usageTypeDomain: undefined, usageTypeLabel: "—" };

  const codeRaw =
    ext?.valueCode != null && String(ext.valueCode).trim() !== ""
      ? String(ext.valueCode).trim()
      : ext?.valueCoding?.code != null &&
          String(ext.valueCoding.code).trim() !== ""
        ? String(ext.valueCoding.code).trim()
        : null;

  if (codeRaw) {
    const domain = CODE_TO_USAGE_TYPE[codeRaw];
    if (domain) {
      const tab = domain as UsageTabValue;
      const label = USAGE_TABS.find((t) => t.value === tab)?.label ?? codeRaw;
      return { usageTypeDomain: tab, usageTypeLabel: label };
    }
    return { usageTypeDomain: undefined, usageTypeLabel: codeRaw };
  }

  const display = ext?.valueCoding?.display?.trim();
  if (display) return { usageTypeDomain: undefined, usageTypeLabel: display };

  const valueString = ext?.valueString?.trim();
  if (valueString)
    return { usageTypeDomain: undefined, usageTypeLabel: valueString };

  return { usageTypeDomain: undefined, usageTypeLabel: "—" };
}

function questionnaireTitleFromExtension(
  questionnaireResponse: any,
): string | null {
  const extensions = questionnaireResponse?.extension;
  if (!Array.isArray(extensions)) return null;
  const ext = extensions.find(
    (e: any) => e?.url === QUESTIONNAIRE_TITLE_EXTENSION_URL,
  );
  const t = String(ext?.valueString ?? "").trim();
  return t ? t : null;
}

function questionnaireTitleFallback(questionnaireResponse: any): string {
  const titleFromExt = questionnaireTitleFromExtension(questionnaireResponse);
  if (titleFromExt) return titleFromExt;

  const questionnaireRef =
    typeof questionnaireResponse?.questionnaire === "string"
      ? questionnaireResponse.questionnaire
      : questionnaireResponse?.questionnaire?.reference;
  const t = String(questionnaireRef ?? "").trim();
  return t || "Questionário";
}

function authoredDateLabel(questionnaireResponse: any): string {
  const authoredRaw = String(questionnaireResponse?.authored ?? "").trim();
  if (!authoredRaw) return "—";
  const d = dayjs(authoredRaw);
  if (!d.isValid()) return authoredRaw;
  return d.format("DD/MM/YYYY");
}

/** Referência do autor (FHIR aceita Reference ou array de Reference em alguns perfis). */
function primaryAuthorReference(
  questionnaireResponse: any,
): string | undefined {
  const a = questionnaireResponse?.author;
  if (!a) return undefined;
  if (Array.isArray(a)) {
    const r = String(a[0]?.reference ?? "").trim();
    return r || undefined;
  }
  const r = String(a?.reference ?? "").trim();
  return r || undefined;
}

/**
 * Indica resposta preenchida por terceiro em nome do paciente do `subject`
 * (ex.: responsável legal no portal).
 */
function isQuestionnaireResponseAnsweredByResponsible(
  questionnaireResponse: any,
): boolean {
  const subj = String(questionnaireResponse?.subject?.reference ?? "").trim();
  const auth = primaryAuthorReference(questionnaireResponse);
  if (!subj || !auth) return false;
  return auth !== subj;
}

export function Questionnaires({
  title,
  questionnaireResponses,
}: {
  title: string;
  questionnaireResponses: any[];
}) {
  const [usageTab, setUsageTab] = useState<UsageTabValue>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQuestionnaireResponse, setSelectedQuestionnaireResponse] =
    useState<fhir5.QuestionnaireResponse | undefined>(undefined);
  const [selectedUsageTypeLabel, setSelectedUsageTypeLabel] = useState<
    string | undefined
  >(undefined);

  const rows = useMemo(() => {
    return (questionnaireResponses ?? []).map((bundleEntry: any) => {
      const questionnaireResponse = bundleEntry?.resource;
      const usage = usageTypeFromQuestionnaireResponse(questionnaireResponse);

      return {
        id:
          questionnaireResponse?.id ??
          `${questionnaireTitleFallback(questionnaireResponse)}-${String(questionnaireResponse?.authored ?? "")}`,
        title: questionnaireTitleFallback(questionnaireResponse),
        date: authoredDateLabel(questionnaireResponse),
        usageTypeDomain: usage.usageTypeDomain,
        usageTypeLabel: usage.usageTypeLabel,
        /** Texto para coluna no prontuário: resposta enviada por responsável vs. próprio paciente. */
        answeredByResponsibleLabel:
          isQuestionnaireResponseAnsweredByResponsible(questionnaireResponse)
            ? "Respondido pelo responsável"
            : "Sim",
        questionnaireResponse,
      };
    });
  }, [questionnaireResponses]);

  const filteredRows = useMemo(() => {
    if (usageTab === "all") return rows;
    if (usageTab === "others") {
      return rows.filter((row) => !row.usageTypeDomain);
    }
    return rows.filter((row) => row.usageTypeDomain === usageTab);
  }, [rows, usageTab]);

  const handleChange = (_event: SyntheticEvent, newValue: UsageTabValue) => {
    setUsageTab(newValue);
  };

  const handleOpenSelected = (row: any) => {
    setSelectedQuestionnaireResponse(row.questionnaireResponse);
    setSelectedUsageTypeLabel(row.usageTypeLabel);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedQuestionnaireResponse(undefined);
    setSelectedUsageTypeLabel(undefined);
  };

  const columns = [
    { column: "Questionário", rowName: "title" },
    { column: "Categoria", rowName: "usageTypeLabel" },
    { column: "Data", rowName: "date" },
    { column: "Autoreferenciado", rowName: "answeredByResponsibleLabel" },
  ];

  return (
    <>
      <Accordion sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="questionnaires-content"
          id="questionnaires-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <AssignmentIcon sx={{ color: "primary.main" }} />
            {title}
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {rows.length || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={usageTab}
              onChange={handleChange}
              aria-label="tabs de categorias de questionários"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {USAGE_TABS.map((tab, index) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  {...a11yProps(index)}
                  sx={{ minHeight: TOUCH_MIN_PX, textTransform: "none" }}
                />
              ))}
            </Tabs>
          </Box>

          {USAGE_TABS.map((tab, index) => {
            const tabRows =
              tab.value === "all"
                ? rows
                : tab.value === "others"
                  ? rows.filter((row) => !row.usageTypeDomain)
                  : rows.filter((row) => row.usageTypeDomain === tab.value);

            return (
              <CustomTabPanel key={tab.value} value={usageTab} index={index}>
                <GenericTable
                  tableColumns={columns}
                  tableRows={tabRows}
                  actions={(rowData: any) => (
                    <QuestionnairesActions
                      row={rowData}
                      onOpenSelected={handleOpenSelected}
                    />
                  )}
                />
              </CustomTabPanel>
            );
          })}
        </AccordionDetails>
      </Accordion>

      <PatientSelectedQuestionnaireDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        questionnaireResponse={selectedQuestionnaireResponse}
        usageTypeLabel={selectedUsageTypeLabel}
      />
    </>
  );
}
